import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Text, { TEXT_COLOR, TEXT_SIZE } from '@components/Text';
import ErrorMessage from '@components/ErrorMessage';
import Input from '@components/Input';
import { updateUserMfa } from '@containers/Admin/containers/UserPage/actions';
import { updateStaffUserMfa } from '@containers/Admin/containers/StaffPage/actions';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import { isValidE164PhoneNumber } from '@utils/phoneNumberUtils';
import {
  Wrapper,
  Title,
  Body,
  Buttons,
  ConfirmButton,
  CancelButton,
  InputWrapper,
  ErrorWrapper,
} from './styles';


const EnterMfaPhoneNumber = ({ userId, userEmail, source, onClose }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const updateMfa = source === 'staff' ? updateStaffUserMfa : updateUserMfa;

  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  const titleText = useMemo(() => {
    const base = t('Authentication.MFA.phoneRequiredTitle', 'Phone number required');
    const email = String(userEmail || '').trim();
    return email ? `${base} - ${email}` : base;
  }, [t, userEmail]);

  const bodyText = useMemo(
    () => t(
      'Authentication.MFA.phoneRequiredBody',
      'This user has no phone number in Cognito. Enter a phone number in E.164 format (example: +972501234567) to enable SMS MFA.',
    ),
    [t],
  );

  const placeholder = useMemo(
    () => t('Authentication.MFA.phonePlaceholder', '+972501234567'),
    [t],
  );

  const onCancel = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  const onConfirm = useCallback(() => {
    const trimmed = String(value || '').trim();
    if (!isValidE164PhoneNumber(trimmed)) {
      setError(true);
      return;
    }

    // Next step: final confirmation popup.
    const confirmTitle = t('Authentication.MFA.adminUpdateConfirmTitle', {
      email: userEmail || '',
      defaultValue: `Update MFA for "${userEmail || ''}"?`,
    });
    const confirmBody = t(
      'Authentication.MFA.adminUpdateConfirmBodySms',
      'This will require MFA via SMS for this user.',
    );

    dispatch(openPopUp(POPUP_TYPES.CONFIRM, {
      titleText: confirmTitle,
      bodyText: confirmBody,
      confirmText: t('Actions.Confirmations.confirm', 'Confirm'),
      cancelText: t('Actions.Instance.cancel', 'Cancel'),
      disableBackdrop: true,
      confirmOnEnter: true,
      onConfirm: (confirm) => confirm && dispatch(updateMfa(userId, 'SMS', trimmed)),
    }));
  }, [dispatch, t, updateMfa, userEmail, userId, value]);

  return (
    <Wrapper>
      <Title>
        <Text size={TEXT_SIZE.T0} textColor={TEXT_COLOR.DARK_BLUE} weight='900'>
          {titleText}
        </Text>
      </Title>

      <Body>
        <Text size={TEXT_SIZE.T3} textColor={TEXT_COLOR.DISABLED}>
          {bodyText}
        </Text>

        <InputWrapper>
          <Input
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onConfirm();
              }
            }}
            placeholder={placeholder}
            error={error}
          />
        </InputWrapper>

        {error && (
          <ErrorWrapper>
            <ErrorMessage
              type='CUSTOM'
              customText={t('Authentication.MFA.phoneInvalid', 'Invalid phone number format')}
            />
          </ErrorWrapper>
        )}
      </Body>

      <Buttons>
        <ConfirmButton className='confirmButton' onClick={onConfirm}>
          {t('Actions.Confirmations.confirm', 'Confirm')}
        </ConfirmButton>
        <CancelButton cancel className='cancelButton' onClick={onCancel}>
          {t('Actions.Instance.cancel', 'Cancel')}
        </CancelButton>
      </Buttons>
    </Wrapper>
  );
};

EnterMfaPhoneNumber.propTypes = {
  userId: PropTypes.string,
  userEmail: PropTypes.string,
  source: PropTypes.string,
  onClose: PropTypes.func,
};

export default EnterMfaPhoneNumber;
